import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Formik, Field, Form, FieldArray } from 'formik';
import { Container, Row, Col, Button } from 'reactstrap';
import * as Yup from 'yup';
import ExerciseHeader from "../header/ExerciseHeader";
import ReCAPTCHA from "react-google-recaptcha";
import "./NewExerciseForm.scss"
import LoadingScreen from "./LoadingScreen";
import { Trans, useTranslation, withTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MobileInfoBar from "./MobileInfoBar";

const NewExerciseSchema = Yup.object().shape({
  studentName: Yup.string().max(100, 'components.form.inputs.name.errors.tooLong'),
  email: Yup.string().email('components.form.inputs.email.errors.notEmail'),
  teacherEmailAddress: Yup.string().email('components.form.inputs.teacher.errors.notEmail'),
  groupMembers: Yup.array().of(
    Yup.object().shape({
      email: Yup.string().email("components.form.inputs.participants.errors.notEmail")
    })
  ),
});


function NewExerciseForm({ exerciseName, exerciseBackgroundImage, slug, enrolmentData, requiredData, recaptchaSiteKey, mobileFriendly }) {
  const formEl = useRef(null);
  const formikRef = useRef(null);
  const { t, i18n } = useTranslation();
  const reCaptchaRef = useRef();
  const location = useLocation();
  const [invalidGroupMembers, setInvalidGroupMembers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  let params = new URLSearchParams(location.search);
  const hasEmail = params.get("email") || "";
  let isValidEnteredEmail = false;
  if (hasEmail) {
    isValidEnteredEmail = /.+@.+\.[A-Za-z]+$/.test(hasEmail);
  }

  const initialValues = {
    studentName: "",
    email: hasEmail,
    schoolName: "",
    teacherEmailAddress: "",
    exerciseMode: enrolmentData && enrolmentData.exerciseMode,
    groupMembers: [{ email: "" }],
    slug
  }

  const [bannerText, setBannerText] = useState({
    translation: "components.mobileInfoBar.notSuitable",
    default: "Toto cvičení není vhodné pro mobilní telefony a tablety.",
    color: "red"
  });

  useEffect(() => {
    const setBannerTextMethod = () => {
      switch (mobileFriendly) {
        case 0:
          setBannerText({
            translation: "components.mobileInfoBar.notSuitable",
            default: "Toto cvičení není vhodné pro mobilní telefony a tablety.",
            color: "red"
          })
          break;
        case 1:
          setBannerText({
            translation: "components.mobileInfoBar.notOptimized",
            default: "Toto cvičení není plně optimalizované pro mobilní a dotyková zařízení. Jeho funkčnost tedy nemusí být srovnatelná s verzí pro počítač.",
            color: "yellow"
          })
          break;
        case 2:
          setBannerText({
            translation: "components.mobileInfoBar.notSuitable",
            default: "Toto cvičení je vhodné pro mobilní telefony a tablety.",
            color: "green"
          })
          break;
        default:
          setBannerText({
            translation: "components.mobileInfoBar.notSuitable",
            default: "Toto cvičení není vhodné pro mobilní telefony a tablety.",
            color: "red"
          })
          break;
      }
    }

    setBannerTextMethod();
  },[mobileFriendly]);

  const addNewGroupMember = (e, values, setValues) => {
    const groupMembers = [...values.groupMembers];
    groupMembers.push({ email: '' });
    setValues({ ...values, groupMembers });
  }

  const removeGroupMember = (e, values, setValues, index) => {
    const groupMembers = [...values.groupMembers];
    groupMembers.splice(index, 1);
    setValues({ ...values, groupMembers });
  }

  const submitForm = async () => {
    setIsSubmitting(true);
    if (enrolmentData) {
      let exerciseModeInput = document.createElement("input");
      exerciseModeInput.type = "hidden";
      exerciseModeInput.name = "exerciseMode";
      exerciseModeInput.value = enrolmentData.exerciseMode;
      formEl.current.appendChild(exerciseModeInput);
      let lmsHashInput = document.createElement("input");
      lmsHashInput.type = "hidden";
      lmsHashInput.name = "lms_hash";
      lmsHashInput.value = enrolmentData.lms_hash;
      formEl.current.appendChild(lmsHashInput);
    }
    await reCaptchaRef.current.executeAsync();
    reCaptchaRef.current.reset();
    formEl.current.submit();
  }

  if(!requiredData) return <LoadingScreen/>;

  const { exid, redirect_url, from } = requiredData;

  if (formikRef.current !== null) {
    if (hasEmail) {
      formikRef.current.setFieldTouched("email", true)
    }
    if (enrolmentData.exerciseMode) {
      formikRef.current.setFieldTouched("exerciseMode", true)
    }
  }

  return (
    <>
      <ExerciseHeader nameOfExercise={exerciseName}/>

      <div className={"overlay"} style={{ backgroundImage: `url(${exerciseBackgroundImage})` }} title={exerciseName}/>
      <Container className="exercise-form-container">
        {mobileFriendly !== 2 && <MobileInfoBar bannerText={bannerText}/>}
        <Row>
          <Col className={"mt-2 basic-text"}><Trans i18nKey={"components.fillForm"}>Pokud budete chtít cvičení uložit, vyplňte následující formulář</Trans></Col>
        </Row>
        <Formik
          initialValues={initialValues}
          validationSchema={NewExerciseSchema}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={async (formData) => {
            await submitForm();
          }}
          innerRef={formikRef}
        >
          {({ errors, touched, values, setValues, handleSubmit }) => (
            <Form className="form-inner" method="POST" action={`/cviceni/${slug}`} ref={formEl} onSubmit={handleSubmit}>
              <Row className={'mb-4'}>
                <Col>
                  <Row className={"form-legend h6 mb-0"}>
                    <Col><Trans i18nKey={"components.form.headers.me"}>Údaje o mně</Trans></Col>
                  </Row>
                  <Row className={"form-row"}>
                    <Col className={"form-item"}>
                      <Row>
                        <Col>
                          <div className={"position-relative form-group"}>
                            <label className={"form-label"} htmlFor={"studentName"}><Trans
                              i18nKey={'components.form.inputs.name.label'}>Mé jméno a příjmení</Trans></label>
                            <Field
                              className={`form-control classname ${errors.studentName && touched.studentName ? "form-control is-invalid" : ''}`}
                              data-cy="form-input-studentName"
                              id="studentName"
                              name="studentName"
                              type="text"
                              placeholder={t('components.form.inputs.name.placeholder')}
                            />
                            {errors.studentName && touched.studentName &&
                            <div data-cy="form-input-studentName-error" className={"invalid-feedback"}><Trans
                              i18nKey={errors.studentName}>Jméno je moc dlouhé</Trans></div>}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col className={"form-item"}>
                      <Row>
                        <Col>
                          <div className={"position-relative form-group"}>
                            <label className={"form-label"} htmlFor={"email"}><Trans
                              i18nKey={'components.form.inputs.email.label'}>Můj e-mail</Trans><small
                              className={"text-small"}> <Trans
                              i18nKey={"components.form.headers.mandatory"}>povinné</Trans></small></label>
                            <Field
                              className={`form-control classname ${errors.email && touched.email ? "form-control is-invalid" : ''}`}
                              data-cy="form-input-email"
                              id="email"
                              name="email"
                              placeholder={t('components.form.inputs.email.placeholder')}
                              type="email"
                              readOnly={hasEmail && isValidEnteredEmail}
                            />
                            {errors.email && touched.email &&
                            <div data-cy="form-input-email-error" className={"invalid-feedback"}><Trans
                              i18nKey={errors.email}>Neplatný e-mail</Trans></div>}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
              {!enrolmentData && (
                <Row className={'mb-4'}>
                  <Col>
                    <Row className={"form-legend h6 mb-0"}>
                      <Col><Trans i18nKey={"components.form.headers.school"}>Údaje o škole a učiteli</Trans></Col>
                    </Row>
                    <Row className={"form-row"}>
                      <Col className={"form-item"}>
                        <Row>
                          <Col>
                            <label className={"form-label"} htmlFor={"schoolName"}><Trans
                              i18nKey={'components.form.inputs.school.label'}>Název a místo mé školy</Trans></label>
                            <Field
                              className={"form-control"}
                              data-cy="form-input-school"
                              id="schoolName"
                              name="schoolName"
                              type="text"
                              placeholder={t('components.form.inputs.school.placeholder')}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col className={"form-item"}>
                        <Row>
                          <Col>
                            <div className={"position-relative form-group"}>
                              <label className={"form-label"} htmlFor={"teacherEmailAddress"}><Trans
                                i18nKey={'components.form.inputs.teacher.label'}>E-mail mého učitele</Trans></label>
                              <Field
                                className={`form-control ${errors.teacherEmailAddress && touched.teacherEmailAddress ? "form-control is-invalid" : ''}`}
                                data-cy="form-input-teacher"
                                id="teacherEmailAddress"
                                name="teacherEmailAddress"
                                placeholder={t('components.form.inputs.teacher.placeholder')}
                                type="email"
                              />
                              {errors.teacherEmailAddress && touched.teacherEmailAddress &&
                              <div data-cy="form-input-teacher-error" className={"invalid-feedback"}><Trans
                                i18nKey={errors.teacherEmailAddress}>Neplatný e-mail</Trans></div>}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
              {!enrolmentData && (
                <Row className={'mb-4'}>
                  <Col>
                    <Row className={"form-legend h6 mb-0"}>
                      <Col><Trans i18nKey={"components.form.headers.exerciseMode"}>Způsob práce</Trans> <small
                        className={"text-small"}><Trans
                        i18nKey={"components.form.headers.mandatory"}>povinné</Trans></small></Col>
                    </Row>
                    <Row className={"row type-of-work"}>
                      <Col className={"form-item"}>
                        <label>
                          <Field data-cy="form-input-exercisemode-0" type="radio" name="exerciseMode" value="0"/>
                          <div className={"form-label-text"}><Trans
                            i18nKey={"components.form.inputs.exerciseMode.independently"}>samostatně</Trans></div>
                        </label>
                      </Col>
                      <Col className={"form-item"}>
                        <label>
                          <Field data-cy="form-input-exercisemode-1" type="radio" name="exerciseMode" value="1"/>
                          <div className={"form-label-text"}><Trans
                            i18nKey={"components.form.inputs.exerciseMode.group"}>ve skupině</Trans></div>
                        </label>
                      </Col>
                      <Col className={"form-item"}>
                        <label>
                          <Field data-cy="form-input-exercisemode-2" type="radio" name="exerciseMode" value="2"/>
                          <div className={"form-label-text"}><Trans
                            i18nKey={"components.form.inputs.exerciseMode.class"}>s celou třídou</Trans></div>
                        </label>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
              {(enrolmentData.exerciseMode === 1 || (enrolmentData === "" && values.exerciseMode === "1")) && (
                <Row className={'mb-4'}>
                  <Col>
                    <Row>
                      <label className={"form-label"} htmlFor={"groupMembers"}><Trans
                        i18nKey={'components.form.headers.participants.main'}>E-maily dalších členů
                        skupiny</Trans></label>
                    </Row>
                    {invalidGroupMembers &&
                    <Row className={'mb-1'}><Col data-cy="form-input-participants-error-all"
                                                 className={"invalid-feedback-group"}><Trans
                      i18nKey={"components.form.inputs.participants.errors.all"}>Jeden nebo více emailů nejsou vyplněny
                      správně</Trans></Col></Row>}
                    <Row className={`group-members mb-2`}>
                      <Col className={"group-members-col"}>
                        <FieldArray
                          name="groupMembers"
                          render={() => {
                            let isInvalidAll = false;

                            let groupMemberArray = values.groupMembers.map((groupMember, index) => {
                              let isInvalid = (errors &&
                                errors.groupMembers &&
                                errors.groupMembers[index] &&
                                errors.groupMembers[index].email &&
                                touched &&
                                touched.groupMembers &&
                                touched.groupMembers[index] &&
                                touched.groupMembers[index].email
                              ) || false;
                              isInvalidAll = isInvalidAll || isInvalid;

                              return (
                                <Row key={index} className={"group-member-row"}>
                                  <Row className={"group-member-row-inputs"}>
                                    <div className={"input-group mb-1"}>
                                      <Field
                                        data-cy="form-input-participants"
                                        className={`form-control ${isInvalid ? 'form-control is-invalid' : 'form-control'}`}
                                        name={`groupMembers[${index}][email]`}
                                        type="email"
                                        placeholder={t('components.form.inputs.participants.placeholder')}
                                      />
                                      <div className={"input-group-append"}>
                                        <Button className="button button-remove"
                                                data-cy="form-input-participants-remove"
                                                onClick={e => removeGroupMember(e, values, setValues, index)}><FontAwesomeIcon
                                          icon={"times"}/></Button>
                                      </div>
                                    </div>
                                  </Row>
                                  <Row>{isInvalid && (
                                    <Row className="invalid-feedback-group">
                                      <Col data-cy="form-input-participants-error-specific">
                                        <Trans i18nKey={errors.groupMembers[index].email}>Neplatný email</Trans>
                                      </Col>
                                    </Row>
                                  )}</Row>
                                </Row>

                              );
                            })
                            if (isInvalidAll !== invalidGroupMembers) {
                              setTimeout(() => setInvalidGroupMembers(isInvalidAll), 0); //FORMIK HAS PROBLEM WITH setState IN RENDER, this solve the issue
                            }
                            return groupMemberArray
                          }
                          }
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <div className={"button button-hl tertiary"}
                             data-cy="form-input-participants-add"
                             onClick={e => addNewGroupMember(e, values, setValues)}>+ <Trans
                          i18nKey={'components.form.inputs.participants.add'}>Přidat dalšího člena</Trans>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
              <input type="hidden" name="slug" value={slug}/>
              <input type="hidden" name="token" value={slug}/>
              <input type="hidden" name="exid" value={exid}/>
              <input type="hidden" name="from" value={from}/>
              <input type="hidden" name="redirect_url" value={redirect_url}/>
              <input type="hidden" id="language" name="language" value={i18n.language || "cs"}/>
              <ReCAPTCHA
                ref={reCaptchaRef}
                sitekey={recaptchaSiteKey}
                size="invisible"
                onChange={value => {
                  document.querySelector("input[name=token]").value = value;
                }}
              />
              <Row className={"submit-button-row"}>
                <Col>
                  <button
                    data-cy="form-input-submit"
                    type="submit"
                    disabled={isSubmitting || Object.keys(errors).length !== 0}
                    className={`button button-hl button-ok ${isSubmitting || Object.keys(errors).length !== 0 ? "disabled" : ""}  ${values.email === "" && Object.keys(errors).length === 0 ? "desaturated" : ""}`}
                    onClick={async e => {
                      e.preventDefault();
                      if (values.email === "") {
                        const result = window.confirm(t("components.form.confirmWindow.message"));
                        if (result) await submitForm();
                      } else {
                        formEl.current.dispatchEvent(
                          new Event("submit", { cancelable: true, bubbles: true })
                        );
                      }
                    }}
                  >
                    {isSubmitting &&
                    (<FontAwesomeIcon icon="spinner" spin/>)
                    }
                    {isSubmitting &&
                    (" ")
                    }
                    {Object.keys(errors).length === 0 && values.email &&
                    <Trans i18nKey={"components.form.buttons.submit"}>Mám vyplněno</Trans>
                    }
                    {Object.keys(errors).length === 0 && !values.email &&
                    <Trans i18nKey={"components.form.buttons.try"}>Vyzkoušet cvičení</Trans>
                    }
                    {
                      Object.keys(errors).length !== 0 &&
                      <Trans i18nKey={"components.form.buttons.invalid"}>Formulář obshauje nevalidní položky</Trans>
                    }
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Container>
    </>
  );
}

export default withTranslation()(NewExerciseForm);
